/**
 * Team Service - API calls for product team operations
 */

import { httpClient, HttpError } from '@/lib/http'
import type {
  TeamResponse,
  TeamMember,
  TeamInvitation,
  MyInvitationsResponse,
  UserSearchResponse,
  InviteUserRequest,
  RespondToInvitationRequest,
  UpdateMemberRoleRequest,
} from '@/lib/types/team'

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

interface ApiError {
  message: string
  code?: string
}

interface GetTeamResult {
  success: boolean
  data?: TeamResponse
  error?: ApiError
}

interface InvitationResult {
  success: boolean
  data?: TeamInvitation
  error?: ApiError
}

interface InvitationsListResult {
  success: boolean
  data?: TeamInvitation[]
  error?: ApiError
}

interface MemberResult {
  success: boolean
  data?: TeamMember
  error?: ApiError
}

interface DeleteResult {
  success: boolean
  error?: ApiError
}

interface MyInvitationsResult {
  success: boolean
  data?: MyInvitationsResponse
  error?: ApiError
}

interface UserSearchResult {
  success: boolean
  data?: UserSearchResponse
  error?: ApiError
}

// ============================================================================
// TEAM DISPLAY (PUBLIC)
// ============================================================================

/**
 * Get team members for a product (public)
 */
export async function getProductTeam(productSlug: string): Promise<GetTeamResult> {
  try {
    const response = await httpClient.get<TeamResponse>(`/products/${productSlug}/team`)
    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Failed to fetch team:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось загрузить команду',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

// ============================================================================
// TEAM MANAGEMENT (OWNER ONLY)
// ============================================================================

/**
 * Invite a user to the team (owner only)
 */
export async function inviteUser(
  productSlug: string,
  inviteeUserId: number,
  role: string
): Promise<InvitationResult> {
  try {
    const request: InviteUserRequest = {
      inviteeUserId,
      role,
    }
    const response = await httpClient.post<TeamInvitation>(
      `/products/${productSlug}/team/invite`,
      request as unknown as Record<string, unknown>
    )
    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Failed to invite user:', error)

    if (error instanceof HttpError) {
      // Handle specific error codes
      if (error.status === 409) {
        return {
          success: false,
          error: {
            message: 'Пользователь уже приглашен или является участником команды',
            code: 'ALREADY_INVITED',
          },
        }
      }
      if (error.status === 403) {
        return {
          success: false,
          error: {
            message: 'Только владелец продукта может приглашать участников',
            code: 'FORBIDDEN',
          },
        }
      }
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось отправить приглашение',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Get all invitations for a product (owner only)
 */
export async function getInvitations(productSlug: string): Promise<InvitationsListResult> {
  try {
    const response = await httpClient.get<TeamInvitation[]>(
      `/products/${productSlug}/team/invitations`
    )
    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Failed to fetch invitations:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось загрузить приглашения',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Revoke a pending invitation (owner only)
 */
export async function revokeInvitation(
  productSlug: string,
  userId: number
): Promise<DeleteResult> {
  try {
    await httpClient.delete(`/products/${productSlug}/team/invitations/${userId}`)
    return {
      success: true,
    }
  } catch (error) {
    console.error('Failed to revoke invitation:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось отозвать приглашение',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Update a team member's role (owner only)
 */
export async function updateMemberRole(
  productSlug: string,
  userId: number,
  role: string
): Promise<MemberResult> {
  try {
    const request: UpdateMemberRoleRequest = { role }
    const response = await httpClient.put<TeamMember>(
      `/products/${productSlug}/team/members/${userId}`,
      request
    )
    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Failed to update member role:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось обновить роль участника',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Remove a team member (owner only)
 */
export async function removeMember(
  productSlug: string,
  userId: number
): Promise<DeleteResult> {
  try {
    await httpClient.delete(`/products/${productSlug}/team/members/${userId}`)
    return {
      success: true,
    }
  } catch (error) {
    console.error('Failed to remove member:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось удалить участника',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

// ============================================================================
// USER INVITATIONS
// ============================================================================

/**
 * Get current user's pending invitations
 */
export async function getMyInvitations(): Promise<MyInvitationsResult> {
  try {
    const response = await httpClient.get<MyInvitationsResponse>('/me/invitations')
    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Failed to fetch my invitations:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось загрузить приглашения',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Respond to an invitation (accept or decline)
 */
export async function respondToInvitation(
  invitationId: number,
  accept: boolean
): Promise<DeleteResult> {
  try {
    const request: RespondToInvitationRequest = { accept }
    await httpClient.post(`/me/invitations/${invitationId}/respond`, request)
    return {
      success: true,
    }
  } catch (error) {
    console.error('Failed to respond to invitation:', error)

    if (error instanceof HttpError) {
      if (error.status === 404) {
        return {
          success: false,
          error: {
            message: 'Приглашение не найдено или уже обработано',
            code: 'NOT_FOUND',
          },
        }
      }
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось ответить на приглашение',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Leave a team (for members)
 */
export async function leaveTeam(productSlug: string): Promise<DeleteResult> {
  try {
    await httpClient.post(`/products/${productSlug}/team/leave`, {})
    return {
      success: true,
    }
  } catch (error) {
    console.error('Failed to leave team:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось покинуть команду',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

// ============================================================================
// USER SEARCH
// ============================================================================

/**
 * Search users by name or username (for invite autocomplete)
 */
export async function searchUsers(query: string): Promise<UserSearchResult> {
  try {
    if (query.length < 2) {
      return {
        success: true,
        data: { users: [] },
      }
    }

    const response = await httpClient.get<UserSearchResponse>('/u/search', {
      params: { q: query },
    })
    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Failed to search users:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось найти пользователей',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}
